import { useEffect, useRef, useState } from "react";
import { Download } from "lucide-react";
import type { Project, ProjectPackageReport, PackageTarget } from "@proof/core";
import { Button, Control } from "./components";

export interface ProjectExportSelection {
  target: PackageTarget;
  partId?: string;
}
export interface ProjectExportServices {
  report(
    project: Project,
    selection: ProjectExportSelection,
    signal: AbortSignal,
  ): Promise<ProjectPackageReport>;
  exportPackage(
    project: Project,
    selection: ProjectExportSelection,
    signal: AbortSignal,
  ): Promise<Blob>;
}
export interface ProjectExportArtifact extends ProjectExportSelection {
  blob: Blob;
  filename: string;
  report: ProjectPackageReport;
}
export interface ProjectExportProps {
  project: Project;
  services: ProjectExportServices;
  initialTarget?: PackageTarget;
  initialPartId?: string;
  allowedTargets?: readonly PackageTarget[];
  onSelectionChange?: (selection: ProjectExportSelection) => void;
  onDownload?: (
    artifact: ProjectExportArtifact,
    signal: AbortSignal,
  ) => void | Promise<void>;
}
const formats: readonly PackageTarget[] = ["native", "idml", "composition"];
export function downloadProjectPackage(artifact: ProjectExportArtifact) {
  const url = URL.createObjectURL(artifact.blob),
    anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = artifact.filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function ProjectExport({
  project,
  services,
  initialTarget,
  initialPartId = "",
  allowedTargets = formats,
  onSelectionChange,
  onDownload = downloadProjectPackage,
}: ProjectExportProps) {
  const [verification, setVerification] = useState<{
    project: Project;
    services: ProjectExportServices;
    target: PackageTarget;
    partId: string;
    report: ProjectPackageReport;
  }>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState<PackageTarget>(
    initialTarget || allowedTargets[0] || "native",
  );
  const [partId, setPartId] = useState(initialPartId);
  const abort = useRef<AbortController>(undefined);
  const valid =
    allowedTargets.length > 0 &&
    allowedTargets.every((value) => formats.includes(value)) &&
    allowedTargets.includes(target) &&
    (target === "native" ||
      !partId ||
      project.parts.some((part) => part.id === partId));
  const latest = useRef({ project, services, target, partId, valid });
  latest.current = { project, services, target, partId, valid };
  const current = (controller: AbortController) =>
    !controller.signal.aborted &&
    latest.current.valid &&
    latest.current.project === project &&
    latest.current.services === services &&
    latest.current.target === target &&
    latest.current.partId === partId;
  const report =
    valid &&
    verification?.project === project &&
    verification.services === services &&
    verification.target === target &&
    verification.partId === partId
      ? verification.report
      : undefined;
  useEffect(() => {
    setVerification(undefined);
    setError("");
    const controller = new AbortController();
    abort.current = controller;
    setBusy(false);
    if (!valid) {
      setError(
        "Choose an available package format and an existing document part.",
      );
      return () => controller.abort();
    }
    void Promise.resolve()
      .then(() => {
        controller.signal.throwIfAborted();
        return services.report(
          structuredClone(project),
          { target, ...(target !== "native" && partId ? { partId } : {}) },
          controller.signal,
        );
      })
      .then((result) => {
        if (!current(controller)) return;
        if (
          result.target !== target ||
          (target !== "native" && (result.conversion?.partId || "") !== partId)
        )
          throw new Error(
            "The host returned a report for different export options.",
          );
        setVerification({
          project,
          services,
          target,
          partId,
          report: structuredClone(result),
        });
      })
      .catch((error) => {
        if (current(controller))
          setError(
            error instanceof Error
              ? error.message
              : "Package verification failed",
          );
      });
    return () => {
      controller.abort();
      abort.current?.abort();
    };
  }, [project, services, target, partId, valid]);
  const download = async () => {
    if (!report || busy || !valid) return;
    setBusy(true);
    setError("");
    const controller = new AbortController();
    abort.current = controller;
    try {
      const selection = {
        target,
        ...(target !== "native" && partId ? { partId } : {}),
      };
      const result = await services.exportPackage(
        structuredClone(project),
        { ...selection },
        controller.signal,
      );
      if (current(controller)) {
        try {
          // Enforce the native Blob brand without rejecting another window.
          Blob.prototype.slice.call(result, 0, 0);
        } catch {
          throw new Error("The host must return a ZIP Blob.");
        }
        // Reads on a Blob from a removed frame can remain pending forever.
        const blob = new Blob([result], { type: result.type });
        await onDownload(
          {
            ...selection,
            blob,
            filename: `${project.id.replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 100)}-${target === "native" ? "portable" : target}.zip`,
            report: structuredClone(report),
          },
          controller.signal,
        );
      }
    } catch (error) {
      if (current(controller))
        setError(
          error instanceof Error ? error.message : "Package download failed",
        );
    } finally {
      if (current(controller)) setBusy(false);
    }
  };
  return (
    <section aria-label="Project export">
      <p>
        Move this project to another Proof Studio instance with its managed
        images and fonts. Import the ZIP through Import document to create a new
        draft.
      </p>
      <Control label="Package format">
        <select
          aria-label="Package format"
          value={target}
          disabled={busy}
          onChange={(event) => {
            const target = event.target.value as PackageTarget;
            setTarget(target);
            onSelectionChange?.({
              target,
              ...(target !== "native" && partId ? { partId } : {}),
            });
          }}
        >
          {allowedTargets.includes("native") && (
            <option value="native">Native project and resources</option>
          )}
          {allowedTargets.includes("idml") && (
            <option value="idml">Editable InDesign IDML (preview)</option>
          )}
          {allowedTargets.includes("composition") && (
            <option value="composition">
              Editable publishing project (preview)
            </option>
          )}
        </select>
      </Control>
      {target !== "native" && (
        <>
          <Control label="Document part">
            <select
              aria-label="Document part"
              value={partId}
              disabled={busy}
              onChange={(event) => {
                const partId = event.target.value;
                setPartId(partId);
                onSelectionChange?.({ target, ...(partId ? { partId } : {}) });
              }}
            >
              <option value="">Main project defaults</option>
              {project.parts.map((part) => (
                <option key={part.id} value={part.id}>
                  {part.name}
                </option>
              ))}
            </select>
          </Control>
          <p>
            {target === "composition"
              ? "Extract the ZIP, keep the Target folder together, open Project.pf and make its packaged Fonts available to the target font manager. Layout files, variables, default sample values and PDF jobs are included. Native application opening and compositor fidelity remain under qualification."
              : "Extract the ZIP, keep Document.idml beside Links and Document fonts, then open the IDML in InDesign. The native snapshot preserves settings the target cannot express. Native InDesign opening and exact compositor parity remain under qualification."}
          </p>
        </>
      )}
      <p>
        This native package preserves variables, sparse part overrides, rules,
        forms, datasets, presets, editable layouts, frames and output profiles.
        It contains a versioned manifest and SHA-256 integrity checks. Up to 50
        MB per package and 8 MB per font face.
      </p>
      {!report && !error && <p role="status">Verifying project resources…</p>}
      {report &&
        report.target === target &&
        (target === "native" ||
          (report.conversion?.partId || "") === partId) && (
          <>
            <p role="status">
              Ready: {report.assets} managed images and {report.fonts} font
              faces.
            </p>
            {report.conversion && (
              <section
                aria-label={
                  target === "composition"
                    ? "Publishing conversion report"
                    : "IDML conversion report"
                }
              >
                <h3>
                  {target === "composition"
                    ? "Publishing conversion report"
                    : "IDML conversion report"}
                </h3>
                <p>
                  {report.conversion.pages} pages ·{" "}
                  {report.conversion.items.length} target objects ·{" "}
                  {report.conversion.variables.length}{" "}
                  {target === "composition"
                    ? "declared variables"
                    : "shared text variables"}{" "}
                  · {report.conversion.fontFiles} desktop fonts ·{" "}
                  {report.conversion.linkedGraphics} linked graphics
                </p>
                <h4>Native settings and review notes</h4>
                <ul>
                  {report.conversion.issues.map((item, index) => (
                    <li key={`${item.code}-${index}`}>{item.message}</li>
                  ))}
                </ul>
              </section>
            )}
            <h3>Destination dependencies</h3>
            {report.dependencies.length ? (
              <>
                <p>
                  Connect these host services in the destination. Runtime
                  records and server provider connections are managed
                  separately.
                </p>
                <ul>
                  {report.dependencies.map((item, index) => (
                    <li key={`${item.sourceId}-${index}`}>
                      {item.kind}:{" "}
                      {project.fields.find(
                        (field) => field.id === item.sourceId,
                      )?.label || item.sourceId}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p>No external host dependencies detected.</p>
            )}
            <p>
              Legacy source records and authored dataset rows are included.
              Store and share the package as project data.
            </p>
            <Button onClick={() => void download()} disabled={busy}>
              <Download size={15} />
              {busy
                ? "Preparing ZIP…"
                : target === "composition"
                  ? "Download publishing ZIP"
                  : target === "idml"
                    ? "Download IDML ZIP"
                    : "Download portable ZIP"}
            </Button>
          </>
        )}
      {error && (
        <p role="alert" className="error-text">
          {error}
        </p>
      )}
    </section>
  );
}
